import { useState, useCallback } from 'react';
import { Check, Flame, Scale, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { AnalyticalBalance, Crucible } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Nitric Acid Safety',
    content: 'Concentrated nitric acid (HNO₃) is highly corrosive and produces toxic brown nitrogen dioxide (NO₂) fumes during the reaction. ALWAYS work in a fume hood with the sash lowered. Wear safety goggles, gloves, and a lab coat. Know emergency procedures.',
    formulas: ['HNO₃ → H⁺ + NO₃⁻', 'Decomposition: 4HNO₃ → 4NO₂ + 2H₂O + O₂']
  },
  2: {
    title: 'Crucible Mass Measurement',
    content: 'The empty crucible mass must be known precisely to calculate the mass of tin oxide formed. Use an analytical balance with 0.001 g precision. Handle the crucible with tongs or a clean tissue to avoid contamination from skin oils.',
    formulas: ['Mass_oxide = Mass_final - Mass_crucible', 'Precision: +/- 0.001 g']
  },
  3: {
    title: 'Weighing the Tin Sample',
    content: 'Weigh approximately 1.0 g of tin foil. Record the exact mass to three decimal places (0.001 g). The tin should be broken into very small pieces to increase surface area for reaction. Avoid handling tin with bare hands.',
    formulas: ['Sn (tin): atomic mass = 118.71 g/mol', 'Target mass: ~1.0 g']
  }
};

export default function Part1Preparation() {
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [tinWeighed, setTinWeighed] = useState(false);
  const [tinMass, setTinMass] = useState(0);
  const [crucibleMass, setCrucibleMass] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [safetyAcknowledged, crucibleMass > 0, tinWeighed].filter(Boolean).length;

  const handleSafety = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handleWeighCrucible = useCallback(() => {
    if (safetyAcknowledged && crucibleMass === 0) {
      const mass = 25 + Math.random() * 5;
      setCrucibleMass(parseFloat(mass.toFixed(3)));
      setCurrentStep(3);
    }
  }, [safetyAcknowledged, crucibleMass]);

  const handleWeighTin = useCallback(() => {
    if (crucibleMass > 0 && !tinWeighed) {
      const mass = 0.95 + Math.random() * 0.1;
      setTinMass(parseFloat(mass.toFixed(3)));
      setTinWeighed(true);
    }
  }, [crucibleMass, tinWeighed]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Scale size={16} />
          <h3>Part I: Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Review', hint: 'HNO₃ fumes are hazardous', done: safetyAcknowledged },
            { id: 2, title: 'Weigh Crucible', hint: 'Record empty crucible mass', done: crucibleMass > 0 },
            { id: 3, title: 'Weigh Tin', hint: 'Add and weigh tin foil', done: tinWeighed }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {tinWeighed && (
          <div className="results-panel">
            <h4>Mass Data</h4>
            <div className="result-row">
              <span>Crucible:</span>
              <span className="value">{crucibleMass.toFixed(3)} g</span>
            </div>
            <div className="result-row">
              <span>Tin:</span>
              <span className="value">{tinMass.toFixed(3)} g</span>
            </div>
            <div className="result-row highlight">
              <span>Total:</span>
              <span className="value">{(crucibleMass + tinMass).toFixed(3)} g</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Empirical Formula Preparation"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Analytical balance', 'Crucible', 'Tin foil']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="prep-workspace">
          {!safetyAcknowledged && (
            <div className="safety-panel" onClick={handleSafety}>
              <Flame size={32} color="#EF4444" />
              <h3>Safety Warning</h3>
              <p>This experiment uses concentrated nitric acid (HNO₃).</p>
              <p>Work in a fume hood. Brown NO₂ fumes are toxic.</p>
              <button className="acknowledge-btn">
                <Check size={16} />
                I Understand
              </button>
            </div>
          )}

          {safetyAcknowledged && (
            <div className="equipment-row">
              <div
                className={`equipment-station ${currentStep === 2 && crucibleMass === 0 ? 'clickable' : ''}`}
                onClick={handleWeighCrucible}
              >
                <Crucible
                  hasContent={tinWeighed}
                  contentColor="#C0C0C0"
                />
                <span className="equipment-label">Porcelain Crucible</span>
              </div>

              <div
                className={`equipment-station ${currentStep === 3 && !tinWeighed ? 'clickable' : ''}`}
                onClick={handleWeighTin}
              >
                <AnalyticalBalance
                  reading={tinWeighed ? crucibleMass + tinMass : crucibleMass > 0 ? crucibleMass : 0}
                  stable={!tinWeighed || crucibleMass > 0}
                  unit="g"
                />
                <span className="equipment-label">Analytical Balance</span>
              </div>

              {/* Tin Foil */}
              <div className="equipment-station">
                <svg viewBox="0 0 50 50" width={70} height={70}>
                  <rect x="10" y="15" width="30" height="20" fill="#A8A8A8" rx="1" />
                  <rect x="12" y="17" width="26" height="16" fill="#C8C8C8" />
                  <text x="25" y="45" fontSize="7" fill="#888" textAnchor="middle">Sn</text>
                </svg>
                <span className="equipment-label">Tin Foil</span>
              </div>
            </div>
          )}

          {tinWeighed && (
            <div className="status-message success">
              <Check size={20} />
              Initial masses recorded. Ready for reaction with HNO₃.
            </div>
          )}

          <div className="theory-box">
            <h4>Empirical Formula Determination</h4>
            <p>
              By reacting tin with nitric acid and heating, we form tin oxide.
              The mass ratio of tin to oxygen allows us to determine the
              empirical formula (SnO or SnO₂).
            </p>
          </div>
        </div>

        <style>{`
          .prep-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-panel {
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid rgba(239, 68, 68, 0.3);
            border-radius: 12px;
            padding: 24px;
            max-width: 400px;
            text-align: center;
            cursor: pointer;
          }

          .safety-panel h3 {
            color: #EF4444;
            margin: 12px 0;
          }

          .safety-panel p {
            color: var(--text-secondary);
            margin: 8px 0;
          }

          .acknowledge-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-top: 16px;
            padding: 10px 20px;
            background: var(--secondary);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .results-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .results-panel h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .result-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .result-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .result-row.highlight {
            background: rgba(0, 168, 107, 0.1);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
